import { useEffect, useState, MouseEvent, useRef } from 'react';
import { ActionModalContent, Dropdown } from 'components/ProfileUIComponents';
import { IOrgDetails } from 'typings/organization';
import { SkeletonLoader } from 'containers/Search/Submissions';
import { Flexbox, Paging, Text } from '@morressier/ts';
import queryString from 'query-string';
import {
  // faSearch, //TODO Search Button Decision
  faChevronRight,
  faChevronLeft,
  faCircleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useForm } from 'react-hook-form';

import {
  Container,
  StyledRadio,
  StyledRadioOrg,
  Department,
  ModalForm,
  SearchInput,
  FieldContainer,
  InlineContainer,
  // SearchButton, //TODO Search Button Decision
  LinkButtonStyle,
  OrgizationNoFoundWarning
} from '../style';

import { VIEWS } from '../EmailAffiliation';
import { Label } from 'containers/Profile/ProfileFormModal/style';
import { countriesOptions } from 'containers/Profile/countriesList';
import { debounce } from 'lodash';
import React from 'react';
import { useFindOrganizations } from '../../hooks';
import {
  LabelContainer,
  PageNumber,
  Label as LabelSearch
} from 'containers/Search/style';
import { SecondaryText } from 'containers/Profile/styles';
import { getInstitutionTypeText } from 'utils/utils';
import { logEvent } from 'utils/eventTracking';

interface IOrganizationProps {
  emailAccess: boolean;
  onUpdate: (ringgoldId: string) => void;
  handleNavigaiton: (nextView: VIEWS) => void;
  onDismiss: () => void;
}
interface FormValues {
  countryAlpha2: string;
  search: string;
  ringgoldId: string;
}

const SearchOrganizationsView = (props: IOrganizationProps) => {
  const {
    register,
    control,
    getValues,
    clearErrors,
    trigger,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>();

  const { emailAccess, handleNavigaiton, onDismiss, onUpdate } = props;

  const searchTerm = useRef<string>('');
  const [queryParams, setQueryParams] = useState('');
  const [allOrgs, setAllOrgs] = useState<IOrgDetails[]>([]);
  const [pageCount, setPageCount] = useState(0);

  const onSubmit = async () => {
    if (isLoading) return;
    const values = getValues();
    const ringgoldId = values?.ringgoldId;
    logEvent(
      'PROFILE_ADD_AFFILIATION_ORGANIZATION_SELECT_ORGANIZATION_CLICKED',
      { organization_id: ringgoldId }
    );
    onUpdate(ringgoldId);
    handleNavigaiton(VIEWS.DetailsView);
  };
  const itemsPerPage = 20;

  const { data, isLoading } = useFindOrganizations(
    queryParams,
    searchTerm.current
  );

  const onSearch = async (term: string, countryCode: string) => {
    const params = queryString.stringify({
      term,
      country_code: countryCode ?? '',
      limit: itemsPerPage,
      offset: 0
    });
    searchTerm.current = term;
    setQueryParams(`?${params}`);
    setSelectedPage(1);
  };

  const onAddOrg = (e: MouseEvent<HTMLButtonElement>) => {
    logEvent(
      'PROFILE_ADD_AFFILIATION_ORGANIZATION_CREATE_ORGANIZATION_CLICKED'
    );
    e.preventDefault();
    handleNavigaiton(VIEWS.UserProvidedAffiliationView);
  };

  const missingOrganizationComponent = (
    <>
      <StyledRadioOrg {...register('ringgoldId', {})}>
        <div>
          <Text fontWeight="bold">{'No matching results'}</Text>
          <Text>
            {
              'If your organization is not already present, please refine your search criteria or add the organization manually.'
            }
          </Text>
          <LinkButtonStyle onClick={onAddOrg}>
            + Add an organization
          </LinkButtonStyle>
        </div>
      </StyledRadioOrg>
    </>
  );

  const noMatchFoundForEmail = (
    <OrgizationNoFoundWarning style={{ marginBottom: '1.5rem' }}>
      <Flexbox>
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{ marginRight: '0.8rem' }}
        />
        <Text size="body2_new" fontWeight="bold">
          {'No matches found for your email domain'}
        </Text>
      </Flexbox>
      <Text size="body2_new" style={{ marginLeft: '2rem' }}>
        {
          'Check your submitted email address or manually choose an organization. Affiliations will remain unverified until a matching domain is found.'
        }
      </Text>
    </OrgizationNoFoundWarning>
  );

  const searchButtonComponent = (
    <></> //TODO Search Button Decision
    // <Controller
    //   name="searchBtn"
    //   control={control}
    //   render={({ field }) => (
    //     <SearchButton
    //       color="secondary"
    //       onClick={async () => {
    //         // const result = await trigger('search');
    //         const values = getValues();
    //         onSearch(values.search, values.countryAlpha2);
    //       }}
    //       iconBefore={<FontAwesomeIcon icon={faSearch} />}
    //     >
    //       Search
    //     </SearchButton>
    //   )}
    // />
  );

  const debounceOnChange = React.useCallback(
    debounce(onSearch, 500, {
      leading: false
    }),
    []
  );

  const topInfoComponent = (
    <Container>
      <Text size="h3_new" fontWeight="bold">
        Choose an organization{' '}
      </Text>
      <Text>
        Choose your organization from our list of established affiliations. If
        your organization is not already present, you can add it manually.
      </Text>
    </Container>
  );

  useEffect(() => {
    updateFilterOrgs();
  }, [data]);

  const onSecondaryClick = () => {
    logEvent(
      'PROFILE_ADD_AFFILIATION_ORGANIZATION_SEARCH_USE_DIFFERENT_EMAIL_CLICKED'
    );
    handleNavigaiton(VIEWS.EmailView);
  };

  const updateFilterOrgs = () => {
    const options = data?.organizations.map(org => {
      return {
        ringgoldId: org.ringgoldId,
        departmentName: org.name,
        organizationName: org.topLevelOrganizationName,
        organizationType: org.organizationType,
        location: org.formattedLocation
      };
    });
    setAllOrgs(options ?? []);
    setPageCount(
      Math.floor((data?.totalCount ? data?.totalCount : 0) / itemsPerPage) + 1
    );
  };

  const [selectedPage, setSelectedPage] = useState(0);

  const handleClick = (selected: number) => () => {
    const newOffset = ((selected - 1) * itemsPerPage) % (data?.totalCount || 0);
    setSelectedPage(selected);
    const values = getValues();
    const params = queryString.stringify({
      term: values.search,
      country_code: values.countryAlpha2 ?? '',
      limit: itemsPerPage,
      offset: newOffset
    });
    searchTerm.current = values.search;
    setQueryParams(`?${params}`);
  };

  const paginationComponent = (
    <div style={{ marginTop: '-3rem' }}>
      <Paging
        currentPage={selectedPage}
        totalPages={pageCount}
        previousButtonText="Prev"
        nextButtonText="Next"
        renderLink={(pageNumber: number, isActive: boolean, label?: string) => {
          if (label) {
            return (
              <LabelContainer label={label} onClick={handleClick(pageNumber)}>
                <LabelSearch
                  size="body1_new"
                  fontWeight="semiBold"
                  color="primaryLink"
                  label={label}
                >
                  {label}
                </LabelSearch>
              </LabelContainer>
            );
          } else {
            return (
              <LabelContainer label={label} onClick={handleClick(pageNumber)}>
                <PageNumber
                  textAs="a"
                  size="body1_new"
                  color={isActive ? 'secondaryBrand' : 'primaryLink'}
                  fontWeight="semiBold"
                  isActive={isActive}
                >
                  {pageNumber}
                </PageNumber>
              </LabelContainer>
            );
          }
        }}
      />
    </div>
  );

  return (
    <ModalForm onSubmit={handleSubmit(onSubmit)}>
      <ActionModalContent
        title="Add affiliation"
        primaryAction={{
          title: 'Continue',
          iconAfter: <FontAwesomeIcon icon={faChevronRight} />,
          type: 'submit'
        }}
        secondaryAction={{
          title: 'Update email',
          iconBefore: <FontAwesomeIcon icon={faChevronLeft} />,
          onClick: onSecondaryClick,
          type: 'button'
        }}
        cautionaryAction={{
          title: 'Exit without saving',
          onClick: () => {
            logEvent('PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CLICKED', {
              step: 'ORGANIZATION_SEARCH'
            });
            onDismiss();
          },
          type: 'button'
        }}
      >
        {topInfoComponent}
        {!emailAccess ? noMatchFoundForEmail : <></>}
        <InlineContainer>
          <FieldContainer>
            <Label>
              <Text size="body1_new" fontWeight="bold">
                Organization
              </Text>
            </Label>
            <Controller
              name="search"
              control={control}
              rules={{
                required: 'Name is Required'
              }}
              render={({ field }) => (
                <SearchInput
                  placeholder="Search by name"
                  onChange={value => {
                    clearErrors('search');
                    field.onChange(value);
                    const values = getValues();
                    debounceOnChange(values.search, values.countryAlpha2);
                  }}
                  error={errors.search?.message}
                />
              )}
            />
          </FieldContainer>
          <FieldContainer>
            <Label>
              <Text size="body1_new" fontWeight="bold">
                Country
              </Text>
            </Label>
            <Controller
              name="countryAlpha2"
              control={control}
              render={({ field }) => (
                <Dropdown
                  options={countriesOptions}
                  isSearchable
                  placeholder="Select Country"
                  error={errors.countryAlpha2?.message}
                  onChange={async value => {
                    const result = await trigger('search', {
                      shouldFocus: true
                    });
                    field.onChange(value);
                    if (result) {
                      const values = getValues();
                      debounceOnChange(values.search, values.countryAlpha2);
                    }
                  }}
                  value="countryAlpha2"
                />
              )}
            />
          </FieldContainer>
          {searchButtonComponent}
        </InlineContainer>
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            {!isLoading && data?.organizations.length == 0 ? (
              <Flexbox flexDirection="column" gap="1rem">
                {missingOrganizationComponent}
              </Flexbox>
            ) : (
              <>
                <Container>
                  {data?.totalCount ? (
                    <Text>
                      {data?.totalCount >= 1000
                        ? data.totalCount + '+'
                        : data.totalCount}{' '}
                      results
                    </Text>
                  ) : (
                    ''
                  )}
                </Container>
                <Flexbox flexDirection="column" gap="1rem">
                  {errors.ringgoldId && (
                    <p style={{ color: 'red' }}>{errors.ringgoldId.message}</p>
                  )}
                  {allOrgs.map(item => (
                    <StyledRadio
                      key={item.ringgoldId}
                      value={item.ringgoldId}
                      {...register('ringgoldId', {
                        required: 'Please select one organization',
                        onChange: event => {
                          logEvent(
                            'PROFILE_ADD_AFFILIATION_ORGANIZATION_SELECT',
                            { organization_id: event?.target?.value }
                          );
                        }
                      })}
                    >
                      <div>
                        <Text fontWeight="bold">{item.departmentName}</Text>
                        <Department>{item.organizationName}</Department>
                        <SecondaryText>
                          {getInstitutionTypeText(item.organizationType)}
                        </SecondaryText>
                        <SecondaryText>{item.location}</SecondaryText>
                      </div>
                    </StyledRadio>
                  ))}
                  {(queryParams.length > 0 &&
                    selectedPage == 0 &&
                    selectedPage == pageCount - 1) ||
                  selectedPage == pageCount ? (
                    missingOrganizationComponent
                  ) : (
                    <></>
                  )}
                </Flexbox>
              </>
            )}
          </>
        )}
        {paginationComponent}
      </ActionModalContent>
    </ModalForm>
  );
};

export default SearchOrganizationsView;
